import { useCallback, useState } from "react";

import { useIsomorphicLayoutEffect } from "./useIsomorphicLayoutEffect";

interface UseIsOverflowingOptions {
  tolerance?: number;
}

export default function useIsOverflowing<T extends HTMLElement>({
  tolerance = 1,
}: UseIsOverflowingOptions = {}) {
  const [node, setNode] = useState<T | null>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const ref = useCallback((node: T | null) => {
    setNode(node);
  }, []);

  useIsomorphicLayoutEffect(() => {
    if (!node) {
      setIsOverflowing(false);
      return;
    }

    const updateOverflow = () => {
      setIsOverflowing(node.scrollHeight > node.clientHeight + tolerance);
    };

    updateOverflow();

    if (typeof ResizeObserver === "undefined") {
      return;
    }

    const resizeObserver = new ResizeObserver(updateOverflow);
    resizeObserver.observe(node);
    Array.from(node.children).forEach(child => resizeObserver.observe(child));

    return () => resizeObserver.disconnect();
  }, [node, tolerance]);

  return { isOverflowing, ref };
}
